<template>
  <div class="container-fluid">
    <Breadcrumbs title="Usuarios eliminados" main="Gestión Usuarios" />
    <div class="card border-0 shadow-sm">
      <div class="card-body p-4">
        <div class="row g-3 mb-4 align-items-end">
          <div class="col-lg-5 col-md-6">
            <label class="form-label fw-semibold" for="buscarEliminado">Buscar</label>
            <input id="buscarEliminado" v-model.trim="busqueda" type="text" class="form-control"
              placeholder="Nombre, correo, Id o administrador" />
          </div>
          <div class="col-lg-3 col-md-4">
            <label class="form-label fw-semibold" for="filtroEstado">Estado</label>
            <select id="filtroEstado" v-model="filtroEstado" class="form-select">
              <option value="todos">Todos</option>
              <option value="eliminados">Eliminados</option>
              <option value="reactivados">Reactivados</option>
            </select>
          </div>
          <div class="col text-md-end">
            <span class="text-muted small">{{ filtrados.length }} de {{ eliminados.length }} registro(s)</span>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-striped table-bordered tabla-eliminados">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Creado</th>
                <th>Eliminado</th>
                <th>Reactivado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="cargando">
                <td colspan="8" class="text-center py-4 text-muted">
                  <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Cargando...
                </td>
              </tr>
              <tr v-else-if="!filtrados.length">
                <td colspan="8" class="text-center py-4 text-muted">No hay registros.</td>
              </tr>
              <template v-else>
                <tr v-for="row in filtrados" :key="row.registroId">
                  <td>{{ row.name }}</td>
                  <td>{{ row.email }}</td>
                  <td><span class="badge badge-rol">{{ row.rolNombre || row.rol }}</span></td>
                  <td>
                    <span class="badge badge-estado" :class="row.vigente ? 'bg-danger' : 'bg-success'">
                      {{ row.vigente ? "eliminado" : "reactivado" }}
                    </span>
                    <small class="d-block text-muted mt-1">
                      Estaba {{ row.statusAnterior === "active" ? "activo" : "inactivo" }}
                    </small>
                  </td>
                  <td class="text-nowrap">{{ formatoFecha(row.creado) }}</td>
                  <td>
                    <div class="text-nowrap">{{ formatoFecha(row.eliminado) }}</div>
                    <small class="text-muted">por {{ row.eliminadoPor || row.eliminadoPorId }}</small>
                  </td>
                  <td>
                    <template v-if="row.reactivado">
                      <div class="text-nowrap">{{ formatoFecha(row.reactivado) }}</div>
                      <small class="text-muted">por {{ row.reactivadoPor || row.reactivadoPorId }}</small>
                    </template>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <div class="acciones">
                      <button type="button" class="btn btn-accion btn-copiar" title="Copiar ID" @click="copiarId(row)">
                        <vue-feather type="copy" size="14" />
                      </button>
                      <button v-if="esAdmin && row.vigente" type="button" class="btn btn-accion btn-activar"
                        title="Activar de nuevo" :disabled="restaurando === row.id" @click="restaurar(row)">
                        <span v-if="restaurando === row.id" class="spinner-border spinner-border-sm" role="status"
                          aria-hidden="true"></span>
                        <vue-feather v-else type="user-check" size="14" />
                      </button>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";

export default {
  name: "UsuariosEliminados",
  data() {
    return {
      eliminados: [],
      busqueda: "",
      filtroEstado: "todos",
      cargando: true,
      restaurando: null,
    };
  },
  computed: {
    esAdmin() {
      return Boolean(this.$store.state.autenticacion.usuarioActual?.esAdmin);
    },
    filtrados() {
      const q = this.busqueda.toLowerCase();
      return this.eliminados.filter((u) => {
        if (this.filtroEstado === "eliminados" && !u.vigente) return false;
        if (this.filtroEstado === "reactivados" && u.vigente) return false;
        if (!q) return true;
        return [u.name, u.email, u.id, u.eliminadoPor, u.eliminadoPorEmail, u.reactivadoPor, u.reactivadoPorEmail].some(
          (v) => String(v ?? "").toLowerCase().includes(q)
        );
      });
    },
  },
  created() {
    this.cargar();
  },
  methods: {
    async cargar() {
      this.cargando = true;
      try {
        this.eliminados = (await this.$store.dispatch("autenticacion/consultarUsuariosEliminados")) || [];
      } catch (e) {
        Swal.fire({ icon: "error", title: "Error", text: e.msg || "No se pudieron cargar los usuarios eliminados." });
      } finally {
        this.cargando = false;
      }
    },
    formatoFecha(valor) {
      if (!valor) return "—";
      const d = new Date(valor);
      if (Number.isNaN(d.getTime())) return "—";
      const dos = (n) => String(n).padStart(2, "0");
      return `${dos(d.getDate())}/${dos(d.getMonth() + 1)}/${d.getFullYear()} ${dos(d.getHours())}:${dos(d.getMinutes())}`;
    },
    async restaurar(row) {
      const escapar = (texto) =>
        String(texto ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const { isConfirmed } = await Swal.fire({
        icon: "question",
        title: "Activar usuario",
        html: `
          <p class="mb-2"><strong>${escapar(row.name)}</strong><br><small>${escapar(row.email)}</small></p>
          <p class="small mb-0">El usuario volverá a Consultar usuarios como activo y podrá iniciar sesión con su
          contraseña actual.</p>
        `,
        showCancelButton: true,
        confirmButtonText: "Activar",
        cancelButtonText: "Cancelar",
      });
      if (!isConfirmed) return;

      this.restaurando = row.id;
      try {
        await this.$store.dispatch("autenticacion/restaurarUsuario", { id: row.id });
        await this.cargar();
        Swal.fire({ icon: "success", title: "Usuario activado", timer: 1800, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: "error", title: "Error", text: e.msg || "No se pudo activar el usuario." });
      } finally {
        this.restaurando = null;
      }
    },
    async copiarId(row) {
      try {
        await navigator.clipboard.writeText(String(row.id));
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "ID copiado", showConfirmButton: false, timer: 1500 });
      } catch (e) {
        Swal.fire({ icon: "info", title: "ID del usuario", text: String(row.id) });
      }
    },
  },
};
</script>

<style scoped>
.tabla-eliminados th {
  font-weight: 700;
  vertical-align: bottom;
}

.tabla-eliminados td {
  vertical-align: top;
}

.badge-rol {
  background-color: #5c61f2;
  padding: 5px 8px;
  font-size: 12px;
}

.badge-estado {
  padding: 5px 8px;
  font-size: 12px;
}

.btn-accion {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 4px;
  color: #fff;
}

.btn-accion:hover {
  color: #fff;
  opacity: 0.9;
}

.btn-copiar {
  background-color: #48a3d7;
}

.btn-activar {
  background-color: #10b981;
}

.btn-accion:disabled {
  color: #fff;
  opacity: 0.65;
}

.acciones {
  display: flex;
  gap: 8px;
}
</style>
